import * as React from "react";
import { styled, Stack, Avatar, Typography, ButtonBase, SxProps, Skeleton } from "@mui/material";

export interface PageFilterProps {
    title: string | number;
    subtitle?: string;
    icon?: React.ReactElement | string;
    active?: boolean;
    variant?: "primary" | "success" | "warning" | "error" | "info" | "background" | "accent";
    small?: boolean;
    onClick?: () => void;
    sx?: SxProps;
    isLoading?: boolean;
}

const PageFilter: React.FC<PageFilterProps> = (props) => {
    const {
        title,
        subtitle,
        variant = "success",
        active = false,
        icon,
        small = false,
        onClick,
        isLoading = false,
        sx,
        ...otherProps
    } = props;

    return (
        <StyledFilter sx={sx} small={small} variant={variant} className={`${active ? "active" : ""}`} onClick={onClick} isLoading={isLoading}>
            <Stack direction="row" alignItems="center" spacing={1.5} {...otherProps} width="100%">
                {isLoading ? (
                    <>
                        <Skeleton variant="circular" width={small ? 24 : 32} height={small ? 24 : 32} />
                        <Stack direction="column" gap={0} flexGrow={1}>
                            <Skeleton variant="text" width="70%" sx={(t) => ({ ...(small ? { ...t.typography.body1 } : { ...t.typography.subtitle1 }) })} />
                            <Skeleton variant="text" width="50%" sx={(t) => ({ ...(small ? { ...t.typography.body2 } : { ...t.typography.body1 }) })} />
                        </Stack>
                    </>
                ) : (
                    <>
                        {icon && typeof icon !== "string" ? (
                            <Avatar
                                sx={{
                                    bgcolor: variant === "background" ? "backgroundAlpha.main" : `${[variant]}.alpha`,
                                    width: small ? 24 : 32,
                                    height: small ? 24 : 32,
                                }}
                            >
                                {icon}
                            </Avatar>
                        ) : (
                            <img loading="lazy" src={icon} width={small ? 24 : 32} height={small ? 24 : 32} />
                        )}

                        <Stack direction="column" gap={0} sx={{ flexShrink: 1, minWidth: 0 }}>

                            <Typography
                                variant={small ? "body1" : "subtitle1"}
                                whiteSpace="nowrap"
                                overflow="hidden"
                                textOverflow="ellipsis"
                            >
                                {title}
                            </Typography>
                            {subtitle && (
                                <Typography
                                    whiteSpace="nowrap"
                                    overflow="hidden"
                                    textOverflow="ellipsis"
                                    variant={small ? "body2" : "body1"}
                                    color="text.tier"
                                >
                                    {subtitle}
                                </Typography>
                            )}
                        </Stack>
                    </>
                )}
            </Stack>
        </StyledFilter>
    );
};

export default PageFilter;

const StyledFilter = styled(ButtonBase, {
    shouldForwardProp: (prop) => prop !== "variant" && prop !== "small" && prop !== "isLoading",
})<Pick<PageFilterProps, "small" | "isLoading"> & { variant: NonNullable<PageFilterProps["variant"]> }>(
    ({ theme, isLoading, variant, small, onClick }) => ({
        padding: small ? theme.spacing(1) : theme.spacing(1.5),
        width: "100%",
        textAlign: "left",
        justifyContent: "flex-start",
        borderRadius: 8,
        background: theme.palette.backgroundAlpha.main,
        cursor: onClick ? "pointer" : "inherit",
        border: "1px solid transparent",
        transition: theme.transitions.create(["background", "border"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        ...(!isLoading && {
            "&:hover": {
                background: theme.palette.backgroundAlpha.hover,
            },
            "&.active": {
                borderColor: variant === "background" ? theme.palette.primary.mainPressed : theme.palette[variant].main,
                backgroundColor: variant === "background" ? theme.palette.backgroundAlpha.hover : theme.palette[variant].alpha,
            },
        }),
        ".MuiAvatar-root": {
            ".MuiSvgIcon-root": {
                color: variant === "background" ? theme.palette.text.tier : theme.palette[variant].main,
                fontSize: small ? 16 : 20,
            },
        },
    })
);
